"use client";

import { useTheme } from "@/contexts/ThemeContext";
import { useIsHydrated } from "@/hooks/useIsHydrated";
import type { ColorModePreference } from "@/lib/design-system/theme";
import { cn } from "@/lib/utils";
import {
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";

const OPTIONS: {
  value: ColorModePreference;
  label: string;
  description: string;
  icon: typeof SunIcon;
}[] = [
  {
    value: "system",
    label: "System",
    description: "Match your device",
    icon: ComputerDesktopIcon,
  },
  {
    value: "light",
    label: "Light",
    description: "Warm bright surfaces",
    icon: SunIcon,
  },
  {
    value: "dark",
    label: "Dark",
    description: "Easier on the eyes at night",
    icon: MoonIcon,
  },
];

type AppearanceSettingsProps = {
  /** When true, omit outer card chrome (used on dedicated Display tab). */
  embedded?: boolean;
  /** Compact segmented control for mobile nav drawers. */
  variant?: "cards" | "sidebar";
  className?: string;
};

export default function AppearanceSettings({
  embedded = false,
  variant = "cards",
  className,
}: AppearanceSettingsProps) {
  const { preference, setPreference } = useTheme();
  const isHydrated = useIsHydrated();
  // Shared shell (Header drawers) must not paint cookie vs localStorage
  // selection differences during hydrate — defer selected chrome until after.
  const activePreference: ColorModePreference | null = isHydrated
    ? preference
    : null;

  if (variant === "sidebar") {
    return (
      <div className={cn("px-4 py-3", className)}>
        <p className="mb-2 text-xs font-medium text-text-light">Appearance</p>
        <div
          className="flex gap-1 rounded-lg bg-secondary p-1"
          role="radiogroup"
          aria-label="Color mode"
        >
          {OPTIONS.map(({ value, label, icon: Icon }) => {
            const selected = activePreference === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={label}
                title={label}
                onClick={() => setPreference(value)}
                className={cn(
                  "flex flex-1 flex-col items-center gap-1 rounded-md px-2 py-2 text-xs font-medium transition-colors",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                  selected
                    ? "bg-surface text-foreground shadow-sm"
                    : "text-text-light hover:text-foreground",
                )}
              >
                <Icon className="h-5 w-5" aria-hidden />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const content = (
    <div
      className={cn(
        "grid grid-cols-1 gap-3 sm:grid-cols-3",
        embedded ? "mt-2" : "mt-4",
      )}
      role="radiogroup"
      aria-label="Color mode"
    >
      {OPTIONS.map(({ value, label, description, icon: Icon }) => {
        const selected = activePreference === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => setPreference(value)}
            className={cn(
              "flex items-center gap-3 rounded-xl border text-left transition-colors",
              "p-4 sm:p-5",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
              selected
                ? "border-primary bg-secondary ring-1 ring-primary ring-inset"
                : "border-border-strong bg-surface hover:bg-secondary",
            )}
          >
            <span
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                selected
                  ? "bg-primary text-white"
                  : "bg-secondary text-foreground",
              )}
              aria-hidden
            >
              <Icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-foreground">
                {label}
              </span>
              <span className="block text-xs text-text-light">
                {description}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <section
      className="sio-surface-card p-5 sm:p-6"
      aria-labelledby="appearance-settings-heading"
    >
      <h2
        id="appearance-settings-heading"
        className="text-lg font-medium text-foreground"
      >
        Display
      </h2>
      <p className="mt-1 text-sm text-text-light">
        Choose how Sell It Off looks on this device. System matches your phone
        or computer.
      </p>
      {content}
    </section>
  );
}
